import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useApp, formatINR, optimizedImage, minVariantPrice, maxVariantPrice } from "../store";
import LotsManager from "../components/LotsManager";
import SiteSettingsPanel from "../components/SiteSettingsPanel";
import BulkImageManager from "../components/BulkImageManager";
import BulkDocumentManager from "../components/BulkDocumentManager";

export default function Admin() {
  const { API, user, authHeaders } = useApp();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [lotsFor, setLotsFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDocsOpen, setBulkDocsOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveOk, setSaveOk] = useState("");

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    axios.get(`${API}/admin/stats`, { headers: authHeaders }).then(r => setStats(r.data));
    axios.get(`${API}/admin/orders`, { headers: authHeaders }).then(r => setOrders(r.data));
    axios.get(`${API}/admin/inquiries`, { headers: authHeaders }).then(r => setInquiries(r.data));
    axios.get(`${API}/products`).then(r => setProducts(r.data));
    axios.get(`${API}/articles?published_only=false`).then(r => setArticles(r.data));
  }, [API, user, authHeaders]);

  if (!user) return <div className="pt-40 text-center font-serif-display text-2xl">Please sign in.</div>;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const saveProduct = async () => {
    setSaveError(""); setSaveOk("");

    // Client-side validation: sanitize variants and surface issues immediately.
    const cleanedVariants = [];
    for (let i = 0; i < (editing.variants || []).length; i++) {
      const v = editing.variants[i];
      const size = (v.size || "").trim();
      const price = parseFloat(v.price);
      if (!size && (isNaN(price) || price === 0)) continue; // fully empty row — drop silently
      if (!size) { setSaveError(`Row ${i + 1}: size is empty. Fill it or click Remove.`); return; }
      if (isNaN(price)) { setSaveError(`Row ${i + 1} (${size}): price is not a valid number.`); return; }
      if (price < 0) { setSaveError(`Row ${i + 1} (${size}): price must be ≥ 0.`); return; }
      cleanedVariants.push({ size, price });
    }
    if (cleanedVariants.length === 0) {
      setSaveError("At least one pack size with a price is required.");
      return;
    }
    if (!editing.name?.trim()) { setSaveError("Product name is required."); return; }
    if (!editing.category?.trim()) { setSaveError("Category is required."); return; }

    const payload = {
      name: editing.name, botanical_name: editing.botanical_name, category: editing.category,
      extraction_method: editing.extraction_method, origin: editing.origin, plant_part: editing.plant_part,
      aroma_profile: editing.aroma_profile, cas_no: editing.cas_no, note: editing.note,
      image_url: editing.image_url, featured: editing.featured,
      variants: cleanedVariants,
    };

    setSaving(true);
    try {
      const r = editing._new
        ? await axios.post(`${API}/products`, payload, { headers: authHeaders })
        : await axios.put(`${API}/products/${editing.id}`, payload, { headers: authHeaders });
      setProducts(prev => editing._new
        ? [r.data, ...prev]
        : prev.map(p => p.id === r.data.id ? r.data : p));
      setSaveOk(editing._new ? "Product created." : "Saved.");
      setTimeout(() => { setSaveOk(""); setEditing(null); }, 900);
    } catch (err) {
      // Try to pull a helpful message from FastAPI/Pydantic error
      let msg = "Save failed. Please try again.";
      const d = err?.response?.data?.detail;
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]) msg = `${d[0].loc?.join('.')}: ${d[0].msg}`;
      else if (err?.message) msg = err.message;
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto" data-testid="admin-page">
      <div className="overline text-copper mb-4">Atelier</div>
      <h1 className="headline-xl text-[clamp(3rem,6vw,6rem)] mb-10">Admin<span className="italic text-[#8A8D8A]">.</span></h1>

      <div className="flex flex-wrap gap-2 mb-10 border-b border-[#E5E1D8]">
        {["dashboard", "products", "orders", "inquiries", "articles", "site settings"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.replace(' ', '-')}`}
            className={`px-6 py-3 overline text-xs transition-all ${tab === t ? "text-copper border-b-2 border-copper -mb-px" : "text-[#5C5E5C] hover:text-[#1E1F1E]"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "dashboard" && stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-1">
          <Stat label="Total Revenue" value={formatINR(stats.revenue)} />
          <Stat label="Paid Orders" value={stats.paid_orders} sub={`of ${stats.total_orders} total`} />
          <Stat label="Products" value={stats.total_products} />
          <Stat label="Customers" value={stats.total_users} sub={`${stats.total_inquiries} inquiries`} />
        </div>
      )}

      {tab === "products" && (
        <div className="space-y-2">
          <div className="flex justify-end gap-2 mb-4 flex-wrap">
            <button
              onClick={() => { setSaveError(""); setSaveOk(""); setEditing({
                _new: true,
                name: "", botanical_name: "", category: "", extraction_method: "",
                origin: "", plant_part: "", aroma_profile: "", cas_no: "", note: "",
                image_url: "", featured: false,
                variants: [{ size: "10ml", price: 0 }],
                long_description: "", usage: "", faqs: [], keywords: [],
              }); }}
              className="btn-ghost"
              data-testid="add-new-product-btn"
            >
              + Add new product
            </button>
            <button onClick={() => setBulkDocsOpen(true)} className="btn-ghost" data-testid="open-bulk-docs">
              📄 Bulk upload GCMS &amp; COA
            </button>
            <button onClick={() => setBulkOpen(true)} className="btn-primary" data-testid="open-bulk-images">
              📷 Re-upload all product photos
            </button>
          </div>
          {products.map(p => (
            <div key={p.id} className="grid md:grid-cols-6 gap-4 items-center py-4 border-b border-[#E5E1D8]" data-testid={`admin-product-${p.id}`}>
              <img src={optimizedImage(p.image_url, 150)} alt={p.name} loading="lazy" decoding="async" className="w-16 h-20 object-cover" />
              <div className="md:col-span-2">
                <div className="font-serif-display text-xl">{p.name}</div>
                <div className="overline text-[10px] text-[#8A8D8A]">{p.category}</div>
              </div>
              <div className="text-sm text-[#5C5E5C]">{p.origin}</div>
              <div className="text-sm">{formatINR(minVariantPrice(p))} - {formatINR(maxVariantPrice(p))}</div>
              <div className="flex items-center gap-3 justify-end">
                <button onClick={() => setLotsFor(p)} className="overline text-xs text-[#5C5E5C] hover:text-copper" data-testid={`lots-${p.id}`}>Lots</button>
                <button onClick={() => { setSaveError(""); setSaveOk(""); setEditing(JSON.parse(JSON.stringify(p))); }} className="overline text-xs text-copper hover:underline" data-testid={`edit-${p.id}`}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-2" data-testid="admin-orders-tab">
          {orders.length === 0 && <p className="text-[#8A8D8A]">No orders yet.</p>}
          {orders.map(o => <OrderRow key={o.id} o={o} API={API} authHeaders={authHeaders} setOrders={setOrders} />)}
        </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-2">
          {inquiries.length === 0 && <p className="text-[#8A8D8A]">No inquiries yet.</p>}
          {inquiries.map(i => (
            <details key={i.id} className="border-b border-[#E5E1D8] py-4" data-testid={`admin-inquiry-${i.id}`}>
              <summary className="grid md:grid-cols-5 gap-4 items-center cursor-pointer">
                <div className="font-serif-display text-lg">{i.name}</div>
                <div className="text-xs text-[#5C5E5C]">{i.company || "—"}</div>
                <div className="text-xs">{i.product_name || "General"}</div>
                <div className="text-xs">{i.quantity || "—"}</div>
                <div className="text-xs text-[#8A8D8A] text-right">{new Date(i.created_at).toLocaleDateString()}</div>
              </summary>
              <div className="mt-4 text-sm text-[#5C5E5C] pl-4 border-l-2 border-copper">
                <p className="mb-2"><strong>Email:</strong> {i.email} | <strong>Phone:</strong> {i.phone || "—"}</p>
                <p className="whitespace-pre-wrap">{i.message}</p>
              </div>
            </details>
          ))}
        </div>
      )}

      {tab === "articles" && (
        <div className="space-y-3" data-testid="admin-articles-tab">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[#5C5E5C]">{articles.length} article{articles.length !== 1 ? "s" : ""}</p>
            <button
              onClick={() => setEditingArticle({ _new: true, slug: "", title: "", excerpt: "", body_md: "", tags: [], cover_image_url: "", published: true })}
              className="btn-primary"
              data-testid="new-article-btn"
            >+ New article</button>
          </div>
          {articles.length === 0 && <p className="text-[#8A8D8A]">No articles yet.</p>}
          {articles.map(a => (
            <div key={a.id} className="grid md:grid-cols-12 gap-4 items-center py-4 border-b border-[#E5E1D8]" data-testid={`admin-article-${a.slug}`}>
              <div className="md:col-span-5">
                <div className="font-serif-display text-lg">{a.title}</div>
                <div className="text-xs text-[#8A8D8A] font-mono">/blog/{a.slug}</div>
              </div>
              <div className="md:col-span-3 text-xs text-[#5C5E5C]">{(a.tags || []).join(", ")}</div>
              <div className="md:col-span-2 text-xs text-[#5C5E5C]">{new Date(a.created_at).toLocaleDateString()}</div>
              <div className="md:col-span-1">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${a.published ? "bg-[#3C6348]/10 text-[#3C6348]" : "bg-[#EBE4D8]"}`}>
                  {a.published ? "Live" : "Draft"}
                </span>
              </div>
              <div className="md:col-span-1 text-right">
                <button onClick={() => setEditingArticle({ ...a })} className="overline text-xs text-copper hover:underline" data-testid={`edit-article-${a.slug}`}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingArticle && (
        <ArticleEditor
          article={editingArticle}
          api={API}
          authHeaders={authHeaders}
          onClose={() => setEditingArticle(null)}
          onSaved={(saved) => {
            setArticles(prev => editingArticle._new
              ? [saved, ...prev]
              : prev.map(p => p.id === saved.id ? saved : p)
            );
            setEditingArticle(null);
          }}
          onDeleted={(id) => {
            setArticles(prev => prev.filter(p => p.id !== id));
            setEditingArticle(null);
          }}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" data-testid="edit-product-modal">
          <div className="bg-[#FAF9F6] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <h3 className="font-serif-display text-3xl mb-6">
              {editing._new ? "Add new product" : `Edit · ${editing.name}`}
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Inp label="Name" v={editing.name} on={v => setEditing({ ...editing, name: v })} />
              <Inp label="Botanical name" v={editing.botanical_name} on={v => setEditing({ ...editing, botanical_name: v })} />
              <Inp label="Category" v={editing.category} on={v => setEditing({ ...editing, category: v })} />
              <Inp label="Extraction" v={editing.extraction_method} on={v => setEditing({ ...editing, extraction_method: v })} />
              <Inp label="Origin" v={editing.origin} on={v => setEditing({ ...editing, origin: v })} />
              <Inp label="Plant Part" v={editing.plant_part} on={v => setEditing({ ...editing, plant_part: v })} />
              <Inp label="CAS No." v={editing.cas_no} on={v => setEditing({ ...editing, cas_no: v })} />
              <div className="col-span-2">
                <label className="overline text-[10px] block mb-2">Product Image</label>
                <div className="flex gap-3 items-start">
                  <input
                    type="url"
                    value={editing.image_url || ""}
                    onChange={e => setEditing({ ...editing, image_url: e.target.value })}
                    className="input-underline flex-1"
                    placeholder="Image URL"
                  />
                  <label className="border border-dashed border-[#E5E1D8] hover:border-copper px-3 py-2 text-xs cursor-pointer flex items-center gap-2 whitespace-nowrap">
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async e => {
                        const file = e.target.files?.[0]; if (!file) return;
                        const fd = new FormData(); fd.append("file", file);
                        const r = await axios.post(`${API}/upload`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
                        setEditing({ ...editing, image_url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` });
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
                {editing.image_url && <img src={editing.image_url} alt="preview" className="w-32 h-32 object-cover mt-3" />}
              </div>
              <div className="col-span-2">
                <label className="overline text-[10px] block mb-2">Aroma Profile</label>
                <textarea value={editing.aroma_profile || ""} onChange={e => setEditing({ ...editing, aroma_profile: e.target.value })} className="input-underline" rows={2} />
              </div>
              <div className="col-span-2">
                <label className="overline text-[10px] block mb-2">Pack Sizes &amp; Pricing (INR)</label>
                <p className="text-[10px] text-[#8A8D8A] mb-3">Add custom sizes like 10ml, 30ml, 50ml — anything. Remove sizes you don&apos;t sell.</p>
                <div className="space-y-2">
                  {editing.variants.map((v, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        value={v.size}
                        placeholder="Size (e.g. 50ml)"
                        onChange={e => {
                          const next = [...editing.variants]; next[i] = { ...v, size: e.target.value };
                          setEditing({ ...editing, variants: next });
                        }}
                        className="input-underline col-span-5"
                        data-testid={`variant-size-${i}`}
                      />
                      <input
                        type="number"
                        value={v.price}
                        placeholder="Price ₹"
                        onChange={e => {
                          const next = [...editing.variants]; next[i] = { ...v, price: e.target.value };
                          setEditing({ ...editing, variants: next });
                        }}
                        className="input-underline col-span-5"
                        data-testid={`variant-price-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = editing.variants.filter((_, idx) => idx !== i);
                          setEditing({ ...editing, variants: next });
                        }}
                        className="col-span-2 text-xs text-[#9E3C3C] hover:underline"
                        data-testid={`remove-variant-${i}`}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setEditing({ ...editing, variants: [...editing.variants, { size: "", price: 0 }] })}
                  className="mt-3 text-xs text-copper hover:underline overline"
                  data-testid="add-variant"
                >
                  + Add pack size
                </button>
              </div>
              <label className="col-span-2 flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={!!editing.featured} onChange={e => setEditing({ ...editing, featured: e.target.checked })} />
                Featured on home
              </label>
            </div>
            <div className="flex gap-3 mt-8 items-center flex-wrap">
              <button
                onClick={saveProduct}
                disabled={saving}
                className={`btn-primary ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid="save-product"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                onClick={() => { setEditing(null); setSaveError(""); setSaveOk(""); }}
                disabled={saving}
                className="btn-ghost"
              >
                Cancel
              </button>
              {saveError && (
                <p className="text-[#9E3C3C] text-xs w-full mt-2" data-testid="product-save-error">✗ {saveError}</p>
              )}
              {saveOk && (
                <p className="text-[#3C8A5C] text-xs w-full mt-2" data-testid="product-save-ok">✓ {saveOk}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "site settings" && <SiteSettingsPanel />}

      {lotsFor && (
        <LotsManager productId={lotsFor.id} productName={lotsFor.name} onClose={() => setLotsFor(null)} />
      )}

      {bulkOpen && <BulkImageManager onClose={() => setBulkOpen(false)} />}
      {bulkDocsOpen && <BulkDocumentManager onClose={() => setBulkDocsOpen(false)} />}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-sand p-8">
      <div className="overline text-[10px] text-[#8A8D8A] mb-3">{label}</div>
      <div className="font-serif-display text-4xl">{value}</div>
      {sub && <div className="text-xs text-[#8A8D8A] mt-1">{sub}</div>}
    </div>
  );
}
function Inp({ label, v, on }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-2">{label}</label>
      <input value={v || ""} onChange={e => on(e.target.value)} className="input-underline" />
    </div>
  );
}

function OrderRow({ o, API, authHeaders, setOrders }) {
  const [open, setOpen] = useState(false);
  const c = o.customer || {};
  const items = o.items || [];
  const totalQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const phoneDigits = (c.phone || "").replace(/\D/g, "");
  const pay = o.payment || {};
  const isPaid = (o.status || "").includes("paid");
  const created = o.created_at ? new Date(o.created_at) : null;

  const waText = encodeURIComponent(
    `Hi ${c.name || ""}, your House of Absolutes order #${o.id.slice(0, 8).toUpperCase()} (${formatINR(o.total)}) is being processed. We will share dispatch details shortly.`
  );

  return (
    <div className="border-b border-[#E5E1D8]" data-testid={`admin-order-${o.id}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full grid md:grid-cols-7 gap-4 items-center py-4 text-left hover:bg-[#FAF8F2] transition-colors px-2"
        data-testid={`admin-order-toggle-${o.id}`}
      >
        <div className="font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</div>
        <div className="md:col-span-2">
          <div className="text-sm font-medium">{c.name}</div>
          <div className="text-xs text-[#8A8D8A]">{c.email}</div>
        </div>
        <div className="text-xs">
          <div className="overline text-[10px] text-[#8A8D8A] mb-0.5">Phone</div>
          <span className="text-copper">{c.phone || "—"}</span>
        </div>
        <div className="text-xs text-[#5C5E5C]">
          {created ? created.toLocaleDateString() : "—"}
          <div className="text-[10px] text-[#8A8D8A]">{created ? created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}</div>
          <div className="text-[10px] text-[#8A8D8A] mt-1">{totalQty} item{totalQty !== 1 ? "s" : ""}</div>
        </div>
        <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 w-fit ${isPaid ? "bg-[#3C6348]/10 text-[#3C6348]" : "bg-[#EBE4D8]"}`}>
          {(o.status || "").replace("_", " ")}
        </span>
        <div className="font-serif-display text-lg text-right">
          {formatINR(o.total)}
          <div className="overline text-[9px] text-[#8A8D8A] mt-1">{open ? "Hide ↑" : "View details ↓"}</div>
        </div>
      </button>

      {open && (
        <div className="px-2 pb-8 pt-2 bg-[#FAF8F2]/60" data-testid={`admin-order-detail-${o.id}`}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Items */}
            <div className="lg:col-span-2">
              <div className="overline text-[10px] text-[#8A8D8A] mb-3">Items ordered ({totalQty} unit{totalQty !== 1 ? "s" : ""})</div>
              <div className="space-y-2" data-testid={`admin-order-items-${o.id}`}>
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-3 py-2 border-b border-[#E5E1D8] text-sm" data-testid={`admin-order-item-${o.id}-${idx}`}>
                    <div className="col-span-6">
                      <div className="font-medium">{it.name}</div>
                      <div className="text-xs text-[#8A8D8A]">Size: {it.size}</div>
                      {it.product_id && <div className="text-[10px] text-[#8A8D8A] font-mono">{it.product_id}</div>}
                    </div>
                    <div className="col-span-2 text-center">
                      <div className="overline text-[9px] text-[#8A8D8A]">Qty</div>
                      <div className="font-serif-display text-lg">{it.quantity}</div>
                    </div>
                    <div className="col-span-2 text-right">
                      <div className="overline text-[9px] text-[#8A8D8A]">Unit</div>
                      <div>{formatINR(it.price)}</div>
                    </div>
                    <div className="col-span-2 text-right">
                      <div className="overline text-[9px] text-[#8A8D8A]">Line</div>
                      <div className="font-medium">{formatINR(it.price * it.quantity)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {o.notes && (
                <div className="mt-6">
                  <div className="overline text-[10px] text-[#8A8D8A] mb-2">Customer notes</div>
                  <p className="text-sm whitespace-pre-wrap text-[#3C3D3C]">{o.notes}</p>
                </div>
              )}
            </div>

            {/* Right column */}
            <div className="space-y-6">
              {/* Bill summary */}
              <div>
                <div className="overline text-[10px] text-[#8A8D8A] mb-3">Bill summary</div>
                <dl className="text-sm space-y-1.5">
                  <Line k="Subtotal" v={formatINR(o.subtotal)} />
                  {o.discount > 0 && <Line k={`Discount${o.coupon_code ? ` (${o.coupon_code})` : ""}`} v={`- ${formatINR(o.discount)}`} accent="#3C6348" />}
                  <Line k="GST (18%)" v={formatINR(o.tax)} />
                  <Line k="Shipping" v={o.shipping ? formatINR(o.shipping) : "Free"} />
                  <div className="border-t border-[#E5E1D8] my-2" />
                  <Line k="Total" v={formatINR(o.total)} bold />
                </dl>
              </div>

              {/* Shipping address */}
              <div>
                <div className="overline text-[10px] text-[#8A8D8A] mb-2">Ship to</div>
                <div className="text-sm leading-relaxed" data-testid={`admin-order-address-${o.id}`}>
                  <div className="font-medium">{c.name}</div>
                  <div>{c.address_line1}</div>
                  {c.address_line2 && <div>{c.address_line2}</div>}
                  <div>{c.city}, {c.state} {c.postal_code}</div>
                  <div>{c.country}</div>
                </div>
              </div>

              {/* Contact + payment */}
              <div>
                <div className="overline text-[10px] text-[#8A8D8A] mb-2">Contact</div>
                <div className="text-sm space-y-1">
                  <div><a className="text-copper hover:underline" href={`mailto:${c.email}`}>{c.email}</a></div>
                  <div><a className="text-copper hover:underline" href={`tel:${c.phone}`}>{c.phone}</a></div>
                  {phoneDigits && (
                    <a
                      href={`https://wa.me/${phoneDigits}?text=${waText}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[#3C6348] hover:underline text-sm mt-1"
                      data-testid={`admin-order-whatsapp-${o.id}`}
                    >Message on WhatsApp →</a>
                  )}
                </div>
              </div>

              <div>
                <div className="overline text-[10px] text-[#8A8D8A] mb-2">Payment</div>
                <div className="text-xs font-mono space-y-1 text-[#3C3D3C]">
                  <div>Status: <span className={isPaid ? "text-[#3C6348]" : "text-[#9E3C3C]"}>{(o.status || "—").replace(/_/g, " ")}</span></div>
                  {pay.method && <div>Method: {pay.method.replace(/_/g, " ")}</div>}
                  {pay.utr_reference && <div>UTR / Ref: <span className="select-all">{pay.utr_reference}</span></div>}
                  {pay.payer_name && <div>Paid by: {pay.payer_name}</div>}
                  {pay.note && <div>Note: {pay.note}</div>}
                  {pay.submitted_at && <div>Submitted: {new Date(pay.submitted_at).toLocaleString()}</div>}
                  <div>Verified: {pay.verified ? "✓ yes" : "✗ no"}</div>
                </div>
                {!pay.verified && pay.utr_reference && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Confirm you've verified payment (UTR: ${pay.utr_reference}) with your bank?`)) return;
                      try {
                        await axios.post(`${API}/admin/orders/${o.id}/verify-payment`, {}, { headers: authHeaders });
                        const r = await axios.get(`${API}/admin/orders`, { headers: authHeaders });
                        setOrders(r.data);
                      } catch (e) {
                        alert(e?.response?.data?.detail || "Failed to verify payment");
                      }
                    }}
                    className="mt-3 text-xs bg-[#3C6348] text-[#FAF9F6] px-4 py-2 hover:opacity-90"
                    data-testid={`verify-payment-${o.id}`}
                  >
                    ✓ Mark payment verified
                  </button>
                )}

                <FulfillmentPanel o={o} API={API} authHeaders={authHeaders} setOrders={setOrders} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Line({ k, v, bold, accent }) {
  return (
    <div className={`flex justify-between ${bold ? "font-serif-display text-lg" : ""}`} style={accent ? { color: accent } : undefined}>
      <dt className="text-[#5C5E5C]">{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

const FULFILLMENT_STAGES = [
  { key: "created",           label: "Created",            hint: "Customer just placed order" },
  { key: "payment_submitted", label: "Payment Submitted",  hint: "UTR received, awaiting verification" },
  { key: "paid",              label: "Paid",               hint: "Payment verified — start processing" },
  { key: "processing",        label: "Processing",         hint: "Packing the oils" },
  { key: "packed",            label: "Packed",             hint: "Ready for courier pickup" },
  { key: "dispatched",        label: "Dispatched",         hint: "Handed to courier — customer notified" },
  { key: "delivered",         label: "Delivered",          hint: "Order complete" },
  { key: "cancelled",         label: "Cancelled",          hint: "Order cancelled" },
];

function FulfillmentPanel({ o, API, authHeaders, setOrders }) {
  const currentStatus = o.status || "created";
  const fulfillment = o.fulfillment || {};
  const [status, setStatus] = useState(currentStatus);
  const [tracking, setTracking] = useState(fulfillment.tracking_number || "");
  const [courier, setCourier] = useState(fulfillment.courier || "");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const currentIdx = FULFILLMENT_STAGES.findIndex(s => s.key === currentStatus);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/orders/${o.id}/status`,
        { status, tracking_number: tracking, courier },
        { headers: authHeaders });
      const r = await axios.get(`${API}/admin/orders`, { headers: authHeaders });
      setOrders(r.data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const dirty = status !== currentStatus || tracking !== (fulfillment.tracking_number || "") || courier !== (fulfillment.courier || "");
  const notifiesCustomer = ["processing", "packed", "dispatched", "delivered", "cancelled"].includes(status) && status !== currentStatus;

  return (
    <div className="mt-6 pt-6 border-t border-[#E5E1D8]" data-testid={`fulfillment-${o.id}`}>
      <div className="overline text-[10px] text-[#8A8D8A] mb-3">Fulfillment</div>

      {/* Timeline pill row */}
      <div className="flex flex-wrap gap-1 mb-4">
        {FULFILLMENT_STAGES.filter(s => s.key !== "cancelled").map((s, i) => {
          const done = i <= currentIdx && currentStatus !== "cancelled";
          const active = s.key === currentStatus;
          return (
            <div
              key={s.key}
              className={`text-[9px] uppercase tracking-widest px-2 py-1 ${
                active ? "bg-[#1E1F1E] text-[#FAF9F6]" :
                done ? "bg-[#3C6348]/10 text-[#3C6348]" :
                       "bg-[#EBE4D8] text-[#8A8D8A]"
              }`}
              title={s.hint}
            >
              {s.label}
            </div>
          );
        })}
        {currentStatus === "cancelled" && (
          <div className="text-[9px] uppercase tracking-widest px-2 py-1 bg-[#9E3C3C] text-[#FAF9F6]">Cancelled</div>
        )}
      </div>

      <div className="space-y-3">
        <label className="block">
          <div className="overline text-[9px] text-[#8A8D8A] mb-1">Change status</div>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="input-underline w-full text-xs"
            data-testid={`fulfillment-status-${o.id}`}
          >
            {FULFILLMENT_STAGES.map(s => (
              <option key={s.key} value={s.key}>{s.label} — {s.hint}</option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <div className="overline text-[9px] text-[#8A8D8A] mb-1">Tracking #</div>
            <input
              value={tracking}
              onChange={e => setTracking(e.target.value)}
              placeholder="AWB / consignment"
              className="input-underline w-full text-xs"
              data-testid={`fulfillment-tracking-${o.id}`}
            />
          </label>
          <label className="block">
            <div className="overline text-[9px] text-[#8A8D8A] mb-1">Courier</div>
            <input
              value={courier}
              onChange={e => setCourier(e.target.value)}
              placeholder="BlueDart, DHL…"
              className="input-underline w-full text-xs"
              data-testid={`fulfillment-courier-${o.id}`}
            />
          </label>
        </div>
        {notifiesCustomer && (
          <div className="text-[10px] text-copper italic">
            ✉ Customer will receive an email when you save this change.
          </div>
        )}
        <button
          onClick={save}
          disabled={!dirty || saving}
          className={`btn-primary text-xs w-full justify-center disabled:opacity-40 ${savedFlash ? "bg-[#3C6348]" : ""}`}
          data-testid={`fulfillment-save-${o.id}`}
        >
          {savedFlash ? "✓ Saved" : saving ? "Saving…" : "Update fulfillment"}
        </button>
      </div>

      {(fulfillment.tracking_number || fulfillment.courier) && (
        <div className="mt-4 text-[10px] font-mono text-[#8A8D8A] space-y-0.5" data-testid={`fulfillment-history-${o.id}`}>
          {fulfillment.stage_dispatched_at && <div>Dispatched: {new Date(fulfillment.stage_dispatched_at).toLocaleString()}</div>}
          {fulfillment.stage_delivered_at && <div>Delivered: {new Date(fulfillment.stage_delivered_at).toLocaleString()}</div>}
        </div>
      )}
    </div>
  );
}


function ArticleEditor({ article, api, authHeaders, onClose, onSaved, onDeleted }) {
  const [a, setA] = useState({ ...article, tags: (article.tags || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(null); // "cover" | "body" | null

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        slug: a.slug.trim().toLowerCase().replace(/\s+/g, "-"),
        title: a.title,
        excerpt: a.excerpt,
        cover_image_url: a.cover_image_url,
        body_md: a.body_md,
        tags: a.tags.split(",").map(t => t.trim()).filter(Boolean),
        published: a.published,
      };
      if (article._new) {
        const r = await axios.post(`${api}/articles`, payload, { headers: authHeaders });
        onSaved(r.data);
      } else {
        const r = await axios.put(`${api}/articles/${article.id}`, payload, { headers: authHeaders });
        onSaved(r.data);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${api}/articles/${article.id}`, { headers: authHeaders });
      onDeleted(article.id);
    } catch (e) {
      setErr(e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" data-testid="article-editor">
      <div className="bg-[#FAF9F6] w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
        <h3 className="font-serif-display text-3xl mb-6">{article._new ? "New article" : "Edit article"}</h3>
        <div className="space-y-4 text-sm">
          <Inp label="Title" v={a.title} on={v => setA({ ...a, title: v })} />
          <Inp label="Slug (URL: /blog/SLUG)" v={a.slug} on={v => setA({ ...a, slug: v })} />
          <div>
            <label className="overline text-[10px] block mb-2">Excerpt (1–2 lines shown in listings)</label>
            <textarea value={a.excerpt} onChange={e => setA({ ...a, excerpt: e.target.value })} className="input-underline w-full" rows={2} data-testid="article-excerpt" />
          </div>
          <div>
            <label className="overline text-[10px] block mb-2">Cover image (upload or paste URL)</label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={a.cover_image_url}
                onChange={e => setA({ ...a, cover_image_url: e.target.value })}
                className="input-underline flex-1"
                placeholder="Paste URL or click Upload →"
                data-testid="article-cover-url"
              />
              <label className="border border-dashed border-[#E5E1D8] hover:border-copper px-3 py-2 text-xs cursor-pointer flex items-center gap-2 whitespace-nowrap">
                {uploading === "cover" ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploading === "cover"}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    try {
                      setUploading("cover");
                      const fd = new FormData(); fd.append("file", file);
                      const r = await axios.post(`${api}/upload`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
                      setA(prev => ({ ...prev, cover_image_url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
                    } catch (er) {
                      setErr(er.response?.data?.detail || "Upload failed");
                    } finally {
                      setUploading(null);
                    }
                  }}
                  className="hidden"
                  data-testid="article-cover-upload"
                />
              </label>
            </div>
            {a.cover_image_url && (
              <img src={a.cover_image_url} alt="Cover preview" className="w-40 h-24 object-cover mt-3 border border-[#E5E1D8]" />
            )}
          </div>
          <Inp label="Tags (comma-separated)" v={a.tags} on={v => setA({ ...a, tags: v })} />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline text-[10px]">Body (Markdown — # heading, ## subheading, **bold**, _italic_, [link](url), - list, ![alt](img-url))</label>
              <label className="border border-dashed border-[#E5E1D8] hover:border-copper px-3 py-1 text-[10px] cursor-pointer flex items-center gap-2 whitespace-nowrap overline">
                {uploading === "body" ? "Uploading…" : "+ Image in body"}
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploading === "body"}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    try {
                      setUploading("body");
                      const fd = new FormData(); fd.append("file", file);
                      const r = await axios.post(`${api}/upload`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
                      const imgUrl = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
                      const altText = file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
                      const snippet = `\n\n![${altText}](${imgUrl})\n\n`;
                      setA(prev => ({ ...prev, body_md: (prev.body_md || "") + snippet }));
                    } catch (er) {
                      setErr(er.response?.data?.detail || "Upload failed");
                    } finally {
                      setUploading(null);
                    }
                  }}
                  className="hidden"
                  data-testid="article-body-image-upload"
                />
              </label>
            </div>
            <textarea value={a.body_md} onChange={e => setA({ ...a, body_md: e.target.value })} className="input-underline w-full font-mono text-xs" rows={20} data-testid="article-body" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={a.published} onChange={e => setA({ ...a, published: e.target.checked })} data-testid="article-published" />
            Published (visible at /blog)
          </label>
        </div>
        {err && <p className="text-[#9E3C3C] text-xs mt-4" data-testid="article-error">{err}</p>}
        <div className="flex justify-between mt-8">
          {!article._new ? (
            <button onClick={del} className="text-xs text-[#9E3C3C] underline" data-testid="article-delete">Delete article</button>
          ) : <div />}
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-ghost" data-testid="article-cancel">Cancel</button>
            <button onClick={save} disabled={busy || !a.title || !a.slug} className="btn-primary" data-testid="article-save">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

